import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bottleImg } from '../mock';

// Product-page image viewer: the studio plate plus any lifestyle shots.
// Falls back to a single image when a product has no extra shots.
export default function ProductGallery({ product }) {
  // lead with the lifestyle shot so the page matches the card that was clicked
  const images = [...(product.shots || []), bottleImg(product.slug)];
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState({});

  const shown = images.filter((_, i) => !failed[i]);
  const idx = Math.min(active, Math.max(shown.length - 1, 0));

  return (
    <div>
      <div
        className="relative rounded-lg overflow-hidden aspect-square w-full"
        style={{
          background: `radial-gradient(700px 300px at 50% 100%, ${product.accent}25, transparent 60%), linear-gradient(180deg, #FBF6EC 0%, #F0E6D3 100%)`
        }}
      >
        {shown.map((src, i) => (
          i === idx ? (
            <img
              key={src}
              src={src}
              alt={`${product.name} — view ${i + 1}`}
              width={900}
              height={900}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={i === 0 ? 'high' : 'low'}
              onError={() => setFailed(f => ({ ...f, [i]: true }))}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : null
        ))}

        {/* gold corner ticks */}
        <span className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#C9A961]/70 pointer-events-none" />
        <span className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#C9A961]/70 pointer-events-none" />

        {/* Prev / Next arrows */}
        {shown.length > 1 && (
          <>
            <button
              onClick={() => setActive((idx - 1 + shown.length) % shown.length)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center transition touch-manipulation z-10"
            >
              <ChevronLeft className="w-4 h-4 text-[#1A2332]" />
            </button>
            <button
              onClick={() => setActive((idx + 1) % shown.length)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center transition touch-manipulation z-10"
            >
              <ChevronRight className="w-4 h-4 text-[#1A2332]" />
            </button>
          </>
        )}
      </div>

      {shown.length > 1 && (
        <div className="mt-3 flex gap-2.5">
          {shown.map((src, i) => (
            <button
              key={src}
              onClick={() => setActive(i)}
              aria-label={`View image ${i + 1}`}
              aria-current={i === idx}
              className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden border transition-all duration-300 touch-manipulation ${
                i === idx
                  ? 'border-[#C9A961] shadow-md'
                  : 'border-[#E5DCC9] opacity-70 hover:opacity-100 hover:border-[#C9A961]/60'
              }`}
              style={{ background: 'linear-gradient(180deg, #FBF6EC 0%, #F0E6D3 100%)' }}
            >
              <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
